<template>
  <div class="main-content wide">
    <div class="wrap">
      <div class="column">
        <component
          :is="getStep()"
          :form="activeStep"
        />
      </div>
      <div class="column sidebar">
        <template v-if="showDefaultSidebar">
          <section class="info-box">
            <div
              v-for="(item, key) in list"
              :key="key"
              class="item"
            >
              <span :class="['icon', key]" />
              <div class="description">{{ item }}</div>
            </div>
          </section>
          <div
            v-dompurify-html="note"
            class="clarification"
          />
        </template>
        <template v-else>
          <div
            v-if="!isDesktop && pleaseNoteTitle && pleaseNoteTitle !== ''"
            class="gvoBlock"
          >
            <span class="title">{{ pleaseNoteTitle }}</span>
            <div v-dompurify-html="pleaseNoteText" />
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
  import Vue from 'vue'
  import { mapState, mapGetters } from 'vuex'

  export default {
    components: {
      intro: () => import(/* webpackChunkName: "payments-intro" */ 'containers/Pages/Payment/Intro'),
      validation: () => import(/* webpackChunkName: "payments-validation" */ 'containers/Pages/Payment/Validation'),
      defaultForm: () => import(/* webpackChunkName: "payments-default-form" */ 'containers/Pages/Payment/Forms/Default'),
      other: () => import(/* webpackChunkName: "payments-other-form" */ 'containers/Pages/Payment/Forms/Other'),
      extras: () => import(/* webpackChunkName: "payments-extras-form" */ 'containers/Pages/Payment/Forms/Extras'),
      speqForm: () => import(/* webpackChunkName: "payments-speq-form" */ 'containers/Pages/Payment/Forms/SpeqForm.vue'),
    },
    computed: {
      ...mapGetters('device', [
        'isDesktop'
      ]),
      ...mapState('payment/generic', [
        'activeStep',
        'list',
        'note',
        'activeItem'
      ]),
      ...mapState('sidebar', [
        'pleaseNoteTitle',
        'pleaseNoteText',
      ]),

      showDefaultSidebar () {
        if (this.activeStep !== 'defaultForm' &&
          (this.activeStep === 'validation' && !['booking_cancellation', 'change_flight_date_time'].includes(this.activeItem.key))
        ) {
          return true
        } else {
          return !['booking_cancellation', 'change_flight_date_time'].includes(this.activeItem.key)
        }
      }
    },
    created () {
      this.$store.commit('payment/setFormLanguage', Vue.i18n.locale().toUpperCase())
    },
    methods: {
      getStep () {
        if (!this.activeStep) {
          return 'intro'
        }

        if (['intro', 'validation', 'defaultForm', 'extras', 'speqForm'].includes(this.activeStep)) {
          return this.activeStep
        }

        return 'other'
      }
    }
  }
</script>

<style lang="scss" scoped rel="stylesheet/scss">
  @import "~assets/styles/common.scss";

  .wrap {
    display: flex;
    flex-direction: column;
    color: $grey-default;
    margin-bottom: 30px;
    font-weight: 400;
    justify-content: space-between;

    @include bp-mf-tablet {
      margin-top: 11px;
    }

    @include bp-mf-desktop {
      flex-direction: row;
      margin-top: 21px;
    }
  }

  .column {

    @include bp-mf-tablet {
      padding: 0 23px;
    }

    @include bp-mf-desktop {
      padding: 0;
      width: 58%;

      &.sidebar {
        padding-top: 81px;
        width: 32%;
      }
    }

    @include bp-desktop-small {
      padding: 0;

      &.sidebar {
        padding-top: 72px;
      }
    }
  }

  .info-box {
    display: flex;
    flex-direction: column;
    margin-top: 15px;
    padding: 0 19px;

    @include bp-mf-tablet {
      padding: 0;
      flex-direction: row;
      justify-content: space-between;
    }

    @include bp-mf-desktop {
      margin-top: 24px;
      flex-direction: column;
    }

    .item {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding-top: 15px;
      width: 100%;

      @include bp-mf-tablet {
        padding: 20px 30px 0;
        width: calc(100% / 3);
      }

      @include bp-mf-desktop {
        padding: 0;
        flex-direction: row;
        width: 100%;
        align-items: center;
        margin-bottom: 30px;
      }

      .icon {
        display: block;
        width: 50px;
        height: 50px;

        &.form {
          background: url("~assets/images/payment/icon-form.svg") no-repeat;
        }

        &.inbox {
          background: url("~assets/images/payment/icon-inbox.svg") no-repeat;
        }

        &.payment {
          background: url("~assets/images/payment/icon-payment.svg") no-repeat;
        }
      }

      .description {
        text-align: center;
        font-size: em(14);
        line-height: 20px;
        margin: 7px auto 28px auto;

        @include bp-mf-tablet {
          margin-top: 11px;
          margin-bottom: 32px;
        }

        @include bp-mf-desktop {
          margin: 0 0 0 21px;
          width: 75%;
          text-align: left;
          font-size: em(16);
          line-height: 24px;
        }
      }
    }
  }

  .clarification {
    color: $grey-light;
    font-size: em(12);
    line-height: 20px;
    font-weight: 300;
    padding: 0 23px;

    @include bp-mf-tablet {
      padding: 0;
    }
  }

  .gvoBlock {
    margin-top: 20px;
    padding: 23px 24px 28px;
    background-color: rgba(205, 218, 50, 0.05);

    .title {
      display: block;
      margin: 7px 0 10px;
      font-weight: 500;
      text-align: left;
    }

    ::v-deep p {
      margin: 0;
      font-size: em(12);
      line-height: 18px;
    }

    ::v-deep ul {
      list-style: none;
      position: relative;
      margin-bottom: 24px;

      @include bp-mf-tablet {
        margin: 27px 0 0 12px;
      }
    }

    ::v-deep li {
      margin-bottom: 12px;
      font-size: em(14);
      line-height: 20px;
      margin-left: 20px;

      &:before {
        content: "";
        display: block;
        position: absolute;
        width: 8px;
        height: 8px;
        left: 0;
        background-color: $green-base;
        border-radius: 50%;
        margin-top: 5px;

        @include bp-mf-tablet {
          left: -11px;
        }
      }
    }

    ::v-deep a {
      color: $blue-medium;
      text-decoration: underline;

      &:visited {
        color: $blue-medium;
        text-decoration: underline;
      }
    }
  }
</style>
